/**
 * WishlistButton Component
 * Heart icon button to add/remove strategies from wishlist
 */
import { useState, useEffect } from 'react'
import { addToWishlist, removeFromWishlist, checkWishlist } from '../services/marketplaceApi'

interface WishlistButtonProps {
  strategyId: string
  userId?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
  onToggle?: (isInWishlist: boolean) => void
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10', 
  lg: 'w-12 h-12'
}

const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6'
}

// Demo user ID - in production, this would come from auth context
const DEMO_USER_ID = 'user_demo'

export default function WishlistButton({ 
  strategyId, 
  userId = DEMO_USER_ID,
  size = 'md',
  className = '',
  onToggle 
}: WishlistButtonProps) {
  const [isInWishlist, setIsInWishlist] = useState(false)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)

  useEffect(() => {
    const checkStatus = async () => {
      setLoading(true)
      try {
        const response = await checkWishlist(userId, strategyId)
        if (response.success && response.data) {
          setIsInWishlist((response.data as any).is_in_wishlist)
        }
      } catch (err) {
        console.error('Failed to check wishlist:', err)
      } finally {
        setLoading(false)
      }
    }

    checkStatus()
  }, [userId, strategyId])

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (toggling) return

    setToggling(true)
    try {
      if (isInWishlist) {
        await removeFromWishlist(userId, strategyId)
        setIsInWishlist(false)
        onToggle?.(false)
      } else {
        await addToWishlist(userId, strategyId)
        setIsInWishlist(true)
        onToggle?.(true)
      }
    } catch (err) {
      console.error('Failed to toggle wishlist:', err)
    } finally {
      setToggling(false)
    }
  }

  if (loading) {
    return (
      <button 
        className={`${sizeClasses[size]} rounded-full bg-gray-700/50 flex items-center justify-center ${className}`}
        disabled
      >
        <div className="animate-pulse bg-gray-600 rounded-full w-4 h-4" />
      </button>
    )
  }

  return (
    <button
      onClick={handleToggle}
      className={`
        ${sizeClasses[size]} 
        rounded-full 
        flex items-center justify-center 
        transition-all duration-200
        ${isInWishlist 
          ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' 
          : 'bg-gray-700/50 hover:bg-gray-700 text-gray-400 hover:text-red-400'
        }
        ${toggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        ${className}
      `}
      title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      disabled={toggling}
    >
      <svg 
        className={`${iconSizes[size]} transition-transform ${toggling ? 'scale-75' : 'scale-100'}`}
        fill={isInWishlist ? 'currentColor' : 'none'}
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
        />
      </svg>
    </button>
  )
}
